import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";

import KeyIngredientIcon from "../images/key-ingredients.png";
import ProductClaimsIcon from "../images/product-claims.png";
import OtherIcon from "../images/other.png";
import FreeFromIcon from "../images/free-from.png";
import SkinTypesIcon from "../images/skin-types.png";
import SkinSensitiveIcon from "../images/skin-sensitive.png";
import SkinConcernsIcon from "../images/skin-concerns.png";
import { useFilter } from "../context/FilterContext";

import "./GlobalFilter.css";

export const filterParams = {
  "Skin Concerns": "sc",
  "Skin Types": "st",
  "Skin Sensitive": "ss",
  "Key Ingredients": "ki",
  "Product Claims": "pc",
  Other: "ot",
  "Free-form": "ff",
};

const spFilterParams = ["ki", "pc", "ot", "ff"];

const filterIcons = {
  "Key Ingredients": KeyIngredientIcon,
  "Product Claims": ProductClaimsIcon,
  Other: OtherIcon,
  "Free-form": FreeFromIcon,
  "Skin Types": SkinTypesIcon,
  "Skin Sensitive": SkinSensitiveIcon,
  "Skin Concerns": SkinConcernsIcon,
};

export default function GlobalFilter({
  items = {},
  onFilterApplied,
  onFilterChange,
  accordionId = "global-filter-accordion",
}) {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("q");
  const [selectedCounts, setSelectedCounts] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    // Whenever selectedCounts changes, inform parent
    if (onFilterChange) {
      const hasActiveFilters = Object.values(selectedCounts).some(
        (count) => count > 0
      );
      onFilterChange(hasActiveFilters);
    }
  }, [selectedCounts, onFilterChange]);

  const { filters } = useFilter();

  useEffect(() => {
    const counts = {};
    Object.entries(filters).forEach(([param, values]) => {
      counts[param] = values.length;
    });

    setSelectedCounts(counts);

    // Update checked states based on context
    const form = document.getElementById("filter-form");
    if (form) {
      Object.entries(filterParams).forEach(([_, param]) => {
        const values = filters[param] || [];
        const checkboxes = form.querySelectorAll(`input[name="${param}"]`);
        checkboxes.forEach((cb) => {
          cb.checked = values.includes(cb.value);
        });
      });
    }
  }, [filters]);

  const handleCheckboxChange = (e) => {
    const { name } = e.target;

    const checkedItems = document.querySelectorAll(
      `#filter-form input[name="${name}"]:checked`
    );

    setSelectedCounts((prev) => ({
      ...prev,
      [name]: checkedItems.length,
    }));
  };

  const { updateFilters } = useFilter();

  const applyFilter = () => {
    const newParams = new URLSearchParams();

    if (keyword) {
      newParams.set("q", keyword);
    }

    const form = document.getElementById("filter-form");
    const checkboxes = form.querySelectorAll(".filter-checkbox:checked");

    const spValues = [];

    checkboxes.forEach((checkbox) => {
      const name = checkbox.getAttribute("name");
      const value = checkbox.value;
      newParams.append(name, value);

      if (spFilterParams.includes(name)) {
        spValues.push(value);
      }
    });

    // Add condensed sp param
    if (spValues.length > 0) {
      newParams.set("sp", spValues.join(","));
    }

    const currentPath = window.location.pathname;
    navigate(`${currentPath}?${newParams.toString()}`, { replace: true });

    //Save filters to context
    const filtersObj = {};
    newParams.forEach((value, key) => {
      if (!filtersObj[key]) {
        filtersObj[key] = [];
      }
      filtersObj[key].push(value);
    });
    updateFilters(filtersObj);

    // Trigger callback to tell parent to re-fetch / re-filter
    onFilterApplied?.();
  };

  const resetFilter = () => {
    const form = document.getElementById("filter-form");
    const checkboxes = form.querySelectorAll(".filter-checkbox");

    checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });

    setSelectedCounts({});

    const newParams = new URLSearchParams();
    if (keyword) newParams.set("q", keyword);

    const currentPath = window.location.pathname;
    navigate(`${currentPath}?${newParams.toString()}`, { replace: true });

    updateFilters({});
    onFilterApplied?.();
  };

  const buildFilterListItems = (items, searchParam) => {
    const params = searchParams.getAll(searchParam);

    return items.map((item, index) => (
      <div className="mb-2" key={`sub-item-${item.id}`}>
        <div className="form-check">
          <input
            name={searchParam}
            value={item.id}
            className="form-check-input filter-checkbox"
            type="checkbox"
            id={`${searchParam}-${index}-${item.id}`}
            defaultChecked={params.includes(item.id.toString())}
            onChange={handleCheckboxChange}
          />
          <label
            className="form-check-label"
            htmlFor={`${searchParam}-${index}-${item.id}`}
          >
            {item.name}
          </label>
        </div>
      </div>
    ));
  };

  const buildFilterList = (name, items, index, searchParam) => {
    const icon = filterIcons[name];
    const count = selectedCounts[searchParam] || 0;

    return (
      <div className="global-filter-wrapper" key={`filter-item-${index}`}>
        <div className="accordion-item">
          <p className="accordion-header" id={`filter-heading-${index}`}>
            <button
              className="accordion-button collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target={`#filter-${index}`}
              aria-expanded="false"
              aria-controls={`filter-${index}`}
            >
              {icon && (
                <img
                  className="filter-icon me-2"
                  src={icon}
                  alt={`${name} icon`}
                />
              )}
              <span style={{ flexGrow: 1 }}>{name}</span>
              {count > 0 && <span className="filter-count-badge">{count}</span>}
            </button>
          </p>
          <div
            id={`filter-${index}`}
            className="accordion-collapse collapse"
            aria-labelledby={`filter-heading-${index}`}
            data-bs-parent={`#${accordionId}`}
          >
            <div className="accordion-body">
              {buildFilterListItems(items, searchParam)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <form id="filter-form" className="flex-fill">
      {keyword && <input type="hidden" name="q" value={keyword} />}

      <div className="accordion accordion-flush" id={accordionId}>
        {Object.keys(items).map((key, index) =>
          key === "Filters"
            ? items[key].map((pref, prefIndex) =>
                buildFilterList(
                  pref.name,
                  pref.sub_product_preference,
                  `${index}-${prefIndex}`,
                  filterParams[pref.name]
                )
              )
            : buildFilterList(key, items[key], index, filterParams[key])
        )}
      </div>

      <div className="mt-4 text-center">
        <button className="btn btn-white" type="button" onClick={resetFilter}>
          Clear
        </button>
        <button
          className="btn btn-primary text-white"
          type="button"
          onClick={applyFilter}
        >
          Apply
        </button>
      </div>
    </form>
  );
}
